import AwaitLock from "await-lock";

import * as Sentry from "@sentry/react";

import { YieldType } from "../../service/globalTypes";
import { delay, handleErrorResending, handleStatusCodes } from "./utils";
import { AddErrorArgs, ErrorThatCantBeSent, ErrorToResend, RemoveErrorSentWithOtherValueArgs } from "./types";
import { BASE_DELAY_MS, MAX_LENGTH_OF_YIELDS, MAX_NUMBER_OF_RETRIES, TIMEOUT } from "./constants";
import { handleResendValues } from "./utils/handleResendValues";

const tryToSendValues = new AwaitLock();
const addErrorLock = new AwaitLock();

export class PricesResendManager {
  private static instance: PricesResendManager;
  private retrySendValueDelayMs = BASE_DELAY_MS;
  private alreadySending = false;

  private errors: ErrorToResend[] = [];
  private errorsThatCantBeSent: ErrorThatCantBeSent[] = [];

  private constructor() {}

  public static getInstance(): PricesResendManager {
    if (!PricesResendManager.instance) {
      PricesResendManager.instance = new PricesResendManager();
    }

    return PricesResendManager.instance;
  }

  public async addError({ error, statusCodes }: AddErrorArgs) {
    await addErrorLock.acquireAsync({ timeout: TIMEOUT });

    try {
      for (const yieldType of Object.keys(statusCodes)) {
        if (!statusCodes[yieldType]) delete statusCodes[yieldType];
      }

      const errorAlreadyExists = this.errors.find(
        (errorData) => errorData.security.securityProducerId === error.securityProducerId
      );

      if (errorAlreadyExists) {
        const yields = Object.keys(statusCodes);

        yields.forEach((yieldType) => {
          if (!errorAlreadyExists.statusCodes[yieldType]) {
            errorAlreadyExists.statusCodes[yieldType] = statusCodes[yieldType];
          }
        });
      } else {
        this.errors.unshift({ security: error, statusCodes });

        if (!this.alreadySending) {
          this.tryToSendValues();
        }
      }
    } catch (error) {
      console.log("Error while adding error to resend list", error);

      Sentry.captureException({
        message: "Error while trying to send values",
        level: "error",
        data: error,
      });
    } finally {
      addErrorLock.release();
    }
  }

  private async tryToSendValues() {
    this.alreadySending = true;

    while (this.errors.length > 0) {
      await tryToSendValues.acquireAsync({ timeout: TIMEOUT });

      try {
        const errorsCopy = [...this.errors];

        for (const errorData of errorsCopy) {
          const { bid, ask, isin, quoteFeedId, row } = errorData.security;
          const validateStatusCodesResult = handleStatusCodes({
            errorData,
            handleErrorResending,
            errorsThatCantBeSent: this.errorsThatCantBeSent,
          });

          if (!validateStatusCodesResult) continue;

          let currentErrorSentYields: YieldType[] = [];
          const commonSecurityInfo = { isin, quoteFeedID: quoteFeedId };

          for (let tryNumber = 1; tryNumber <= MAX_NUMBER_OF_RETRIES; tryNumber++) {
            await delay(this.retrySendValueDelayMs);

            const { successfullySent } = await handleResendValues({
              ...validateStatusCodesResult,
              bid,
              ask,
              commonSecurityInfo,
              currentErrorSentYields,
              row,
              tryNumber,
              errorData,
            });

            if (successfullySent) break;

            this.retrySendValueDelayMs = this.retrySendValueDelayMs * 2;
          }

          this.retrySendValueDelayMs = BASE_DELAY_MS;
        }
      } catch (error) {
        console.log("Error while trying to send values", error);

        Sentry.captureException({
          message: "Error while trying to send values",
          level: "error",
          data: error,
        });
      } finally {
        tryToSendValues.release();
      }
    }

    this.alreadySending = false;
  }

  public tryToResendErrorThatCantBeSent({ securityProducerId }: { securityProducerId: string }) {
    const errorToResend = this.errorsThatCantBeSent.find(
      (error) => error.security.securityProducerId === securityProducerId
    );

    if (!errorToResend) return;

    const filteredErrors = this.errorsThatCantBeSent.filter(
      (error) => error.security.securityProducerId !== securityProducerId
    );

    this.errorsThatCantBeSent = filteredErrors;

    const { security, statusCodes } = errorToResend;

    this.addError({
      error: security,
      statusCodes,
    });
  }

  public getErrorsThatCantBeSent() {
    return this.errorsThatCantBeSent;
  }

  public removeErrorSentWithOtherValue({
    securityProducerId,
    yieldsSentSuccessfully,
  }: RemoveErrorSentWithOtherValueArgs) {
    const errorToDelete = this.errorsThatCantBeSent.find(
      (error) => error.security.securityProducerId === securityProducerId
    );

    if (!errorToDelete || !yieldsSentSuccessfully.length) return;

    const { statusCodes } = errorToDelete;

    if (yieldsSentSuccessfully.length === MAX_LENGTH_OF_YIELDS && statusCodes.bid && statusCodes.ask) {
      this.errorsThatCantBeSent = this.errorsThatCantBeSent.filter(
        (error) => error.security.securityProducerId !== securityProducerId
      );
      return;
    }

    const yieldType = yieldsSentSuccessfully[0];

    if (statusCodes[yieldType]) {
      delete statusCodes[yieldType];
    }

    if (!Object.keys(statusCodes).length) {
      this.errorsThatCantBeSent = this.errorsThatCantBeSent.filter(
        (error) => error.security.securityProducerId !== securityProducerId
      );
    }
  }

  public getErrors() {
    return this.errors;
  }

  public popErrorsList = () => this.errors.pop();

  public resetManager() {
    this.errors = [];
    this.errorsThatCantBeSent = [];
    this.alreadySending = false;
    this.retrySendValueDelayMs = BASE_DELAY_MS;
  }
}
